/**
 * Floating panel to edit node details and manage nested tree flow.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import FloatingPanel from './FloatingPanel';
import { useStore } from 'store/useStore';
import Select from 'components/ui/Select';
import Field from 'components/ui/Field';
import { childTypeOf, flattenTreeNodes, getDefaultNodePanelPosition, isNodePanelPositionOnScreen, getOutcomeProgressPercent } from 'utils/ostUtils';
import { NodeType, OstNode, NewNodePayload } from 'types';
import { estimateStoryPoints, getRepoScanStatus } from 'api/ai';
import { useFeatureAccess, handleAiLimitReached } from 'pricing';
import { Modal } from 'components/ui';
import MiniPieChart from 'components/ui/MiniPieChart';
import MomentumChart from 'components/ui/MomentumChart';
import ProgressBar from 'components/ui/ProgressBar';
import { getStatusSegments } from 'components/ui/StatusCircle';

const MOSCOW_OPTS = ['Must have', 'Should have', 'Could have', "Won't have (MVP)"];
const VALIDATED_OPTS = ['✅ Validated', '⚠️ Partial', '❌ Assumed'];
const STATUS_OPTS_SOLUTION = ['Idea', 'Validated', 'In progress', 'Done'];
const STATUS_OPTS_STORY = ['Draft', 'Ready to build', 'Pushed to GitHub', 'Done'];

// The Status dropdown is temporarily disabled for solution/story/experiment nodes (product
// decision, not a bug) — gated behind this single flag rather than deleted so the field, its
// options, and the `status` data itself stay intact for a later refactor/repurposing. Flip back
// to true to restore the controls.
const SHOW_NODE_STATUS_DROPDOWN = false;

type NodePanelProps = {
    node: OstNode;
    /** Needed to fetch the outcome's momentum chart activity data. */
    projectId?: string;
    ostId?: string;
    onClose: () => void;
    onAdd: (type: NodeType, parentId: string | null, payload?: NewNodePayload) => void;
    onError: (msg: string) => void;
};

export default function NodePanel({ node, projectId, ostId, onClose, onAdd, onError }: NodePanelProps) {
    const {
        updateNode,
        deleteNode,
        removeRelation,
        canDeleteNode,
        githubRepo,
        nodePanelPosition,
        setNodePanelPosition,
        solutionChildType,
        outcome,
        opportunities,
        solutions,
        userStories,
        connections,
        removeConnection,
        insightLinks: rawInsightLinks,
        insights: rawInsights,
        removeInsightLinkTarget,
        aiModelQuality,
    } = useStore();
    const [form, setForm] = useState({ ...node });
    const [panelPos, setPanelPos] = useState(() =>
        nodePanelPosition && isNodePanelPositionOnScreen(nodePanelPosition)
            ? nodePanelPosition
            : getDefaultNodePanelPosition(),
    );

    // AI t-shirt-size estimation — pro/teams only, requires a prior successful repo
    // scan (see Settings "Scan Codebase"). tryEstimateAccess shows an upgrade prompt
    // itself when the user is on the free tier.
    const { tryAccess: tryEstimateAccess } = useFeatureAccess('ai_estimation');
    const [estimating, setEstimating] = useState(false);
    const [estimateError, setEstimateError] = useState<string | null>(null);
    const [hasScan, setHasScan] = useState(false);
    const [rationaleModalOpen, setRationaleModalOpen] = useState(false);

    const allNodes = flattenTreeNodes({ outcome, opportunities, solutions, userStories });

    // Progress-bar percent, controlled directly by the slider below. Falls back to 0 (empty bar)
    // when unset.
    const progressPercent = getOutcomeProgressPercent(form) ?? 0;

    const outgoingRelations = (form.relations || []).filter((r) => r.direction !== 'incoming');
    const outgoingConnectionKeys = new Set(connections
        .filter((c) => c.fromNodeId === node.id)
        .map((c) => `${c.fromNodeId}:${c.toNodeId}:${c.type}`));
    const filteredOutgoingRelations = outgoingRelations.filter((r) => !outgoingConnectionKeys.has(`${node.id}:${r.nodeId}:${r.type}`));

    const incomingRelations = allNodes
        .filter((n) => n.relations?.some((rel) => rel.nodeId === node.id && rel.direction !== 'incoming'))
        .map((n) => ({ source: n, relation: n.relations?.find((rel) => rel.nodeId === node.id && rel.direction !== 'incoming')! }));
    const incomingConnectionKeys = new Set(connections
        .filter((c) => c.toNodeId === node.id)
        .map((c) => `${c.fromNodeId}:${c.toNodeId}:${c.type}`));
    const filteredIncomingRelations = incomingRelations.filter(({ source, relation }) => !incomingConnectionKeys.has(`${source.id}:${node.id}:${relation.type}`));

    const outgoingConnections = connections.filter((c) => c.fromNodeId === node.id);
    const incomingConnections = connections.filter((c) => c.toNodeId === node.id);

    // Tracks whether the current form change originated from a node prop sync (not user input).
    const isSyncingNode = useRef(false);
    const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Sync form state when the currently selected node changes
    useEffect(() => {
        isSyncingNode.current = true;
        setForm({ ...node });
    }, [node]);

    // Auto-save form changes with a short debounce, skipping syncs triggered by node prop changes.
    useEffect(() => {
        if (isSyncingNode.current) {
            isSyncingNode.current = false;
            return;
        }
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => {
            updateNode(node.type, node.id, form);
        }, 400);
        return () => {
            if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        };
    }, [form, node.type, node.id, updateNode]);

    // Sync panel position when the global row position changes — fall back to the
    // right-aligned default if the stored position is stale (e.g. saved from a wider window).
    useEffect(() => {
        setPanelPos(
            nodePanelPosition && isNodePanelPositionOnScreen(nodePanelPosition)
                ? nodePanelPosition
                : getDefaultNodePanelPosition(),
        );
    }, [nodePanelPosition]);

    // Check whether a successful repo scan exists for the connected repo — gates the
    // "Estimate with AI" button, which requires an architecture document to ground its estimate.
    useEffect(() => {
        let mounted = true;
        if (githubRepo && (node.type === 'story' || node.type === 'experiment')) {
            getRepoScanStatus(githubRepo)
                .then((status) => { if (mounted) setHasScan(!!status.hasScan); })
                .catch(() => { if (mounted) setHasScan(false); });
        } else {
            setHasScan(false);
        }
        return () => { mounted = false; };
    }, [githubRepo, node.type]);

    /** Persists the node panel position when FloatingPanel finishes a drag gesture. */
    const handlePanelMove = useCallback(
        (pos: { x: number; y: number }) => {
            setPanelPos(pos);
            setNodePanelPosition(pos);
        },
        [setNodePanelPosition],
    );

    const field = (key: string, val: string) => setForm((f) => ({ ...f, [key]: val }));

    const del = async () => {
        if (!canDeleteNode(node.type, node.id)) {
            onError('Cannot delete this node — it still has children. Remove child nodes first.');
            return;
        }
        try {
            const { githubError } = await deleteNode(node.type, node.id);
            // The node itself is gone either way; the panel still closes.
            if (githubError) onError(githubError);
            onClose();
        } catch (e) {
            onError(e instanceof Error ? e.message : 'Delete failed. Please try again.');
        }
    };

    const estimate = async () => {
        if (!githubRepo || !tryEstimateAccess()) return;
        setEstimating(true);
        setEstimateError(null);
        try {
            // Ground the estimate relative to sibling stories/experiments already sized
            // in this tree, so sizes stay comparable across the project rather than each
            // being reasoned about in isolation. Falls back to absolute t-shirt-size
            // reasoning (handled server-side) when nothing has been sized yet.
            const calibration = userStories
                .filter((s) => s.id !== node.id && typeof s.storySize === 'string' && s.storySize)
                .slice(0, 20)
                .map((s) => ({
                    title: s.title,
                    summary: (s.asA ? `As a ${s.asA}, I want ${s.iWant || ''}` : (s.assumption || s.description || '')).slice(0, 300),
                    size: s.storySize as string,
                }));

            const result = await estimateStoryPoints(githubRepo, {
                asA: form.asA,
                iWant: form.iWant,
                soThat: form.soThat,
                acceptanceCriteria: form.acceptanceCriteria,
                assumption: form.assumption,
                method: form.method,
                successMetric: form.successMetric,
            }, calibration, aiModelQuality);
            useStore.getState().refreshAiUsage();
            if (result.error) {
                if (!handleAiLimitReached(result)) {
                    setEstimateError(result.error === 'no_scan' ? (result.message || 'Run a repo scan in Settings first.') : result.error);
                }
            } else if (typeof result.storySize === 'string') {
                const rationale = result.rationale || '';
                updateNode(node.type, node.id, { storySize: result.storySize, storySizeRationale: rationale });
                // Update local form state directly — the store update alone doesn't
                // re-render this panel until the node prop round-trips back down, which
                // only happens on next selection, so without this the estimate wouldn't
                // show until the panel is closed and reopened.
                setForm((f) => ({ ...f, storySize: result.storySize, storySizeRationale: rationale }));
            }
        } catch (e) {
            setEstimateError(e instanceof Error ? e.message : 'Failed to estimate story size.');
        }
        setEstimating(false);
    };

    return (
        <FloatingPanel
            x={panelPos.x}
            y={panelPos.y}
            onMove={handlePanelMove}
            defaultWidth={360}
            resizeEdges={['s']}
            className='nodepanel'
            headerClassName={`nodepanel-header nodepanel-header--${node.type}`}
            onClick={(e) => e.stopPropagation()}
            header={
                <>
                    <span className='nodepanel-header-label'>{node.type === 'enablerNode' ? 'enabler' : node.type}</span>
                    <button className='modal-btn nodepanel-close-btn' onClick={onClose}>
                        ✕
                    </button>
                </>
            }
        >
            <Field label='Title'>
                <input
                    className='nodepanel-input'
                    value={form.title || ''}
                    onChange={(e) => field('title', e.target.value)}
                />
            </Field>

            {(node.type === 'outcome' || node.type === 'opportunity' || node.type === 'solution') && (
                <Field label='Description'>
                    <textarea
                        className='nodepanel-input nodepanel-textarea-sm'
                        value={form.description || ''}
                        onChange={(e) => field('description', e.target.value)}
                    />
                </Field>
            )}

            {node.type === 'outcome' && (
                <Field label={`Progress (${Math.round(progressPercent)}%)`}>
                    <ProgressBar percent={progressPercent} onChange={(v) => field('progressPercent', String(v))} />
                </Field>
            )}

            {node.type === 'outcome' && (
                <Field label='Status'>
                    <div className='nodepanel-status-pies'>
                        <div className='nodepanel-status-pie'>
                            <MiniPieChart segments={getStatusSegments(opportunities)} />
                            <span className='nodepanel-status-pie-label'>Opps.</span>
                        </div>
                        <div className='nodepanel-status-pie'>
                            <MiniPieChart segments={getStatusSegments(solutions)} />
                            <span className='nodepanel-status-pie-label'>Solutions</span>
                        </div>
                        <div className='nodepanel-status-pie'>
                            <MiniPieChart segments={getStatusSegments(userStories)} />
                            <span className='nodepanel-status-pie-label'>{solutionChildType === 'experiment' ? 'Experiments' : 'Stories'}</span>
                        </div>
                    </div>
                </Field>
            )}

            {node.type === 'outcome' && projectId && ostId && (
                <Field label='Momentum'>
                    <MomentumChart projectId={projectId} ostId={ostId} />
                </Field>
            )}

            {node.type === 'opportunity' && (
                <Field label='Validated?'>
                    <Select opts={VALIDATED_OPTS} value={form.validated} onChange={(v: string) => field('validated', v)} />
                </Field>
            )}

            {node.type === 'solution' && (
                <>
                    <Field label='MoSCoW'>
                        <Select opts={MOSCOW_OPTS} value={form.moscow} onChange={(v: string) => field('moscow', v)} />
                    </Field>
                    {SHOW_NODE_STATUS_DROPDOWN && (
                        <Field label='Status'>
                            <Select opts={STATUS_OPTS_SOLUTION} value={form.status} onChange={(v: string) => field('status', v)} />
                        </Field>
                    )}
                </>
            )}

            {(node.type === 'story' || node.type === 'enablerNode') && (
                <>
                    <Field label='As a...'>
                        <input
                            className='nodepanel-input'
                            value={form.asA || ''}
                            onChange={(e) => field('asA', e.target.value)}
                        />
                    </Field>
                    <Field label='I want...'>
                        <input
                            className='nodepanel-input'
                            value={form.iWant || ''}
                            onChange={(e) => field('iWant', e.target.value)}
                        />
                    </Field>
                    <Field label='So that...'>
                        <input
                            className='nodepanel-input'
                            value={form.soThat || ''}
                            onChange={(e) => field('soThat', e.target.value)}
                        />
                    </Field>
                    <Field label='Acceptance criteria (one per line)'>
                        <textarea
                            className='nodepanel-input nodepanel-textarea-lg'
                            value={form.acceptanceCriteria || ''}
                            onChange={(e) => field('acceptanceCriteria', e.target.value)}
                        />
                    </Field>
                    {SHOW_NODE_STATUS_DROPDOWN && (
                        <div className="field-mt-neg-4">
                            <Field label='Status'>
                                <Select opts={STATUS_OPTS_STORY} value={form.status} onChange={(v: string) => field('status', v)} />
                            </Field>
                        </div>
                    )}
                    {/* field-mt-neg-8 tightens the gap to the Status dropdown right above it — only
                        appropriate while that field is showing; with Status hidden, MoSCoW instead
                        sits right after the Acceptance criteria textarea and wants the default gap,
                        same as everywhere else in this panel. */}
                    {SHOW_NODE_STATUS_DROPDOWN ? (
                        <div className="field-mt-neg-8">
                            <Field label='MoSCoW'>
                                <Select opts={MOSCOW_OPTS} value={form.moscow} onChange={(v: string) => field('moscow', v)} />
                            </Field>
                        </div>
                    ) : (
                        <Field label='MoSCoW'>
                            <Select opts={MOSCOW_OPTS} value={form.moscow} onChange={(v: string) => field('moscow', v)} />
                        </Field>
                    )}
                    {form.githubUrl && (
                        <Field label='GitHub Issue'>
                            <a href={form.githubUrl} target='_blank' rel='noreferrer' className='nodepanel-link'>
                                {form.githubUrl}
                            </a>
                        </Field>
                    )}
                </>
            )}

            {node.type === 'experiment' && (
                <>
                    <Field label='Assumption'>
                        <input
                            className='nodepanel-input'
                            value={form.assumption || ''}
                            onChange={(e) => field('assumption', e.target.value)}
                        />
                    </Field>
                    <Field label='Method'>
                        <input
                            className='nodepanel-input'
                            value={form.method || ''}
                            onChange={(e) => field('method', e.target.value)}
                        />
                    </Field>
                    {SHOW_NODE_STATUS_DROPDOWN && (
                        <div className="field-mt-neg-6">
                            <Field label='Status'>
                                <Select opts={STATUS_OPTS_STORY} value={form.status} onChange={(v: string) => field('status', v)} />
                            </Field>
                        </div>
                    )}
                    {/* field-mt-neg-6 tightens the gap to the Status dropdown right above it — only
                        appropriate while that field is showing; with Status hidden, MoSCoW instead
                        sits right after the Method input and wants the default gap, same as
                        everywhere else in this panel (and matching Story's MoSCoW above). */}
                    {SHOW_NODE_STATUS_DROPDOWN ? (
                        <div className="field-mt-neg-6">
                            <Field label='MoSCoW'>
                                <Select opts={MOSCOW_OPTS} value={form.moscow} onChange={(v: string) => field('moscow', v)} />
                            </Field>
                        </div>
                    ) : (
                        <Field label='MoSCoW'>
                            <Select opts={MOSCOW_OPTS} value={form.moscow} onChange={(v: string) => field('moscow', v)} />
                        </Field>
                    )}
                    <Field label='Success metric'>
                        <input
                            className='nodepanel-input'
                            value={form.successMetric || ''}
                            onChange={(e) => field('successMetric', e.target.value)}
                        />
                    </Field>
                    {form.githubUrl && (
                        <Field label='GitHub Issue'>
                            <a href={form.githubUrl} target='_blank' rel='noreferrer' className='nodepanel-link'>
                                {form.githubUrl}
                            </a>
                        </Field>
                    )}
                </>
            )}

            {(node.type === 'story' || node.type === 'experiment') && (
                <Field label='Size Estimate'>
                    <div className='nodepanel-estimate-row'>
                        <button
                            className='btn btn-blue'
                            onClick={estimate}
                            disabled={estimating || !githubRepo || !hasScan}
                            title={
                                !githubRepo
                                    ? 'Select a connected GitHub repo in Settings first'
                                    : !hasScan
                                        ? 'Run a repo scan in Settings first'
                                        : undefined
                            }
                        >
                            {estimating ? 'Estimating…' : 'Estimate with AI'}
                        </button>
                        {form.storySize && (
                            <button
                                type='button'
                                className='nodepanel-storysize-badge'
                                onClick={() => setRationaleModalOpen(true)}
                                title='View the AI’s reasoning for this estimate'
                            >
                                {form.storySize}
                            </button>
                        )}
                    </div>
                    {estimateError && <div className='modal-error'>{estimateError}</div>}
                </Field>
            )}

            {rationaleModalOpen && (
                <Modal
                    title={`Size Estimate: ${form.storySize || ''}`}
                    onClose={() => setRationaleModalOpen(false)}
                    closeOnOverlayClick
                >
                    <p className='codebase-summary-text'>
                        {form.storySizeRationale || 'No rationale was recorded for this estimate.'}
                    </p>
                </Modal>
            )}

            {/* ── Evidence from Insights — opportunity nodes only ── */}
            {node.type === 'opportunity' && (() => {
                const insightLinks = rawInsightLinks ?? [];
                const insights = rawInsights ?? [];
                const linksForNode = insightLinks.filter((l) => l.targetNodeIds.includes(node.id));
                return (
                    <div className='nodepanel-section'>
                        <div className='nodepanel-label' style={{ margin: '10px 0 8px' }}>Evidence from Insights</div>
                        {linksForNode.length === 0 ? (
                            <div className='relation-empty-state'>
                                Select text in an insight description and drag it to this node.
                            </div>
                        ) : (
                            <div className='insight-evidence-list'>
                                {linksForNode.map((link) => {
                                    const sourceInsight = insights.find((ins) => ins.id === link.insightId);
                                    return (
                                        <div key={link.id} className='insight-evidence-entry'>
                                            <div className='insight-evidence-header'>
                                                <span className='insight-evidence-source'>
                                                    💡 {sourceInsight?.title ?? 'Insight'}
                                                </span>
                                                <button
                                                    className='relation-delete-btn'
                                                    title='Remove this evidence'
                                                    onClick={() => removeInsightLinkTarget(link.id, node.id)}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                            <blockquote className='insight-evidence-quote'>
                                                "{link.selectedText}"
                                            </blockquote>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })()}

            <div className='nodepanel-section'>
                <div className='nodepanel-label' style={{ margin: '10px 0 8px' }}>Relationships</div>
                {filteredIncomingRelations.length === 0 && incomingConnections.length === 0 && filteredOutgoingRelations.length === 0 && outgoingConnections.length === 0 ? (
                    <div className='relation-empty-state'>
                        Create a relationship by Ctrl + drag from one node to another.
                    </div>
                ) : (
                    <>
                        {(filteredIncomingRelations.length > 0 || incomingConnections.length > 0) && (
                            <div className='relation-group'>
                                {filteredIncomingRelations.map(({ source, relation }) => (
                                    <div key={`legacy-${source.id}-${relation.type}`} className='relation-entry relation-entry--incoming'>
                                        <span className='relation-arrow relation-arrow--in'>←</span>
                                        <div className='relation-entry-content'>
                                            {relation.type !== 'related' && (
                                                <span className='relation-type-pill'>{relation.type}</span>
                                            )}
                                            <span className='relation-node-title'>{source.title}</span>
                                        </div>
                                        <button
                                            className='relation-delete-btn'
                                            onClick={() => {
                                                removeRelation(source.id, node.id);
                                                setForm((prev) => ({
                                                    ...prev,
                                                    relations: (prev.relations || []).filter((r) => r.nodeId !== source.id),
                                                }));
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                                {incomingConnections.map((conn) => {
                                    const source = allNodes.find((n) => n.id === conn.fromNodeId);
                                    return (
                                        <div key={`conn-${conn.id}`} className='relation-entry relation-entry--incoming'>
                                            <span className='relation-arrow relation-arrow--in'>→</span>
                                            <div className='relation-entry-content'>
                                                {conn.type !== 'related' && <span className='relation-type-pill'>{conn.type}</span>}
                                                <span className='relation-node-title'>{source?.title || conn.fromNodeId}</span>
                                            </div>
                                            <button className='relation-delete-btn' onClick={() => removeConnection(conn.id)}>
                                                ✕
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {(filteredOutgoingRelations.length > 0 || outgoingConnections.length > 0) && (
                            <div className='relation-group'>
                                {filteredOutgoingRelations.map((relation) => {
                                    const targetNode = allNodes.find((n) => n.id === relation.nodeId);
                                    return (
                                        <div key={`legacy-${relation.nodeId}-${relation.type}`} className='relation-entry relation-entry--outgoing'>
                                            <div className='relation-entry-content'>
                                                {relation.type !== 'related' && (
                                                    <span className='relation-type-pill'>{relation.type}</span>
                                                )}
                                                <span className='relation-node-title'>{targetNode?.title || relation.nodeId}</span>
                                            </div>
                                            <span className='relation-arrow relation-arrow--out'>→</span>
                                            <button
                                                className='relation-delete-btn'
                                                onClick={() => {
                                                    removeRelation(node.id, relation.nodeId);
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        relations: (prev.relations || []).filter((r) => r.nodeId !== relation.nodeId),
                                                    }));
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    );
                                })}
                                {outgoingConnections.map((conn) => {
                                    const target = allNodes.find((n) => n.id === conn.toNodeId);
                                    return (
                                        <div key={`conn-${conn.id}`} className='relation-entry relation-entry--outgoing'>
                                            <div className='relation-entry-content'>
                                                {conn.type !== 'related' && <span className='relation-type-pill'>{conn.type}</span>}
                                                <span className='relation-node-title'>{target?.title || conn.toNodeId}</span>
                                            </div>
                                            <span className='relation-arrow relation-arrow--out'>→</span>
                                            <button className='relation-delete-btn' onClick={() => removeConnection(conn.id)}>
                                                ✕
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className='nodepanel-actions'>
                <button className='btn btn-danger' onClick={del}>
                    Delete
                </button>
                {childTypeOf(node.type, solutionChildType) && (
                    <button
                        className='btn btn-blue nodepanel-add-fullbtn'
                        onClick={() => onAdd(childTypeOf(node.type, solutionChildType) as NodeType, node.id)}
                    >
                        Add child
                    </button>
                )}
            </div>
        </FloatingPanel>
    );
}
